import type * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { LiveTrace } from "../components/LiveTrace";
import { fetchBlogPosts, fetchProjects } from "../lib/api";

export const Route = createFileRoute("/")({
  component: Home,
});

function Home() {
  const projectsQuery = useQuery({
    queryKey: ["projects"],
    queryFn: fetchProjects,
  });
  const blogQuery = useQuery({ queryKey: ["blog"], queryFn: fetchBlogPosts });

  const featured = projectsQuery.data?.featured.slice(0, 4) ?? [];
  const latestPosts = (blogQuery.data ?? []).slice(0, 3);
  const editorsPicks = (blogQuery.data ?? []).filter((p) => p.featured).slice(0, 2);

  return (
    <>
      <p className="frame-label">
        frame <b>#0</b> — main()
      </p>
      <h1>
        <span className="boot-in" style={{ "--d": "0ms" } as React.CSSProperties}>
          This page is running.
        </span>
        <br />
        <span className="boot-in" style={{ "--d": "140ms" } as React.CSSProperties}>
          Here&apos;s the trace.
        </span>
        <span className="caret caret-h1" aria-hidden="true" />
      </h1>
      <p className="role boot-in" style={{ "--d": "280ms" } as React.CSSProperties}>
        Rounak Kumar Singh — B.Tech student, systems programmer, IIEST Shibpur
      </p>

      <LiveTrace />

      <Link to="/about" className="cta primary">
        step into about()
      </Link>

      <div className="section-gap">
        <div className="section-title">projects[] — featured</div>
        <div className="section-sub">
          // full detail pages, {featured.length} selected out of the project list
        </div>
        {projectsQuery.isLoading && <p className="section-sub">// loading...</p>}
        {projectsQuery.isError && (
          <p className="section-sub">// could not reach the api</p>
        )}
        <div className="mini-cards">
          {featured.map((p) => (
            <Link
              key={p.name}
              className="mini-card"
              to={p.slug ? "/projects/$slug" : "/projects"}
              params={p.slug ? { slug: p.slug } : undefined}
            >
              <div className="sig">{p.name}()</div>
              <div className="desc">{p.description}</div>
            </Link>
          ))}
        </div>
        <Link to="/projects" className="see-all">
          see all projects →
        </Link>
      </div>

      <div className="section-gap">
        <div className="section-title">blog() — recent</div>
        <div className="section-sub">
          // latest posts, and a couple I&apos;d point a stranger to first
        </div>
        {blogQuery.isLoading && <p className="section-sub">// loading...</p>}
        <div className="blog-cols">
          <div>
            <div className="blog-col-label">// latest</div>
            {latestPosts.map((post) => (
              <Link key={post.slug} className="mini-post" to="/blog/$slug" params={{ slug: post.slug }}>
                <div className="date">
                  {post.publishedAt
                    ? new Date(post.publishedAt).toISOString().slice(0, 10)
                    : "unpublished"}
                </div>
                <div className="title">{post.title}</div>
              </Link>
            ))}
          </div>
          <div>
            <div className="blog-col-label">// editor&apos;s picks</div>
            {editorsPicks.map((post) => (
              <Link key={post.slug} className="mini-post" to="/blog/$slug" params={{ slug: post.slug }}>
                <div className="date">featured</div>
                <div className="title">{post.title}</div>
              </Link>
            ))}
          </div>
        </div>
        <Link to="/blog" className="see-all">
          see all posts →
        </Link>
      </div>
    </>
  );
}
